import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { EMPTY, Subject, catchError, forkJoin, switchMap, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-list';
import '@maersk-global/mds-components-core/mc-list-item';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import '@maersk-global/mds-components-core/mc-menu';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import { AuthHelper } from '../../auth/auth-helper';
import { downloadBlob } from '../../helper/download-helpers';
import { buildVscSummaryWorkbook, vscSummaryFileName } from '../../helper/vsc-summary-export';
import { GeoIndex } from '../../models/geography-tree.model';
import { VscListRow, VendorScoreGscViewItem } from '../../models/vsc-gsc-view.model';
import { GeographyService } from '../../services/geography.service';
import { VscKpiCategoryService } from '../../services/vsc-kpi-category.service';
import { ToastrService } from '../../services/toastr-service';
import { parseMonthLabel } from '../vsc-detailed-view/vsc-scorecard.mapper';
import { AemsGeoFilterComponent } from '../../shared-components/aems-geo-filter/aems-geo-filter.component';
import { VscGridComponent } from '../../components/vsc/vsc-grid/vsc-grid.component';

@Component({
  selector: 'app-vsc-list-view',
  imports: [CommonModule, AemsGeoFilterComponent, VscGridComponent],
  templateUrl: './vsc-list-view.component.html',
  styleUrl: './vsc-list-view.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VscListViewComponent implements OnDestroy {
  private readonly scorecardService = inject(VscKpiCategoryService);
  private readonly geoService = inject(GeographyService);
  private readonly toastr = inject(ToastrService);

  @ViewChild('periodContainer') private readonly periodContainer!: ElementRef;

  /** Absent while the grid is replaced by the loading or error state. */
  private readonly grid = viewChild(VscGridComponent);

  private readonly shopCodes = AuthHelper.user?.maintenanceShops?.map((s) => s.maintenanceShopCode) ?? [];
  private readonly shopNameMap = new Map(
    AuthHelper.user?.maintenanceShops?.map((s) => [s.maintenanceShopCode, s.maintenanceShopName]) ?? [],
  );

  private readonly monthOffset = new Date().getDate() <= 12 ? 2 : 1;
  readonly selectedMonth = signal(this.formatMonth(new Date(new Date().getFullYear(), new Date().getMonth() - this.monthOffset, 1)));
  /** Newest month first. */
  readonly monthOptions: string[] = this.buildMonthOptions();
  readonly periodDropdownOpen = signal(false);

  readonly rows = signal<VscListRow[]>([]);
  readonly isLoading = signal(false);
  /** Covers the ExcelJS chunk download as well as building the workbook. */
  readonly isDownloading = signal(false);
  readonly compiledAtTimes = signal<string[]>([]);
  readonly dataError = signal<string | null>(null);
  readonly geoFilterCodes = signal<string[] | null>(null);

  /** Distinct compile runs (newest first); stamps within 5s of each other count as one run. */
  readonly compiledAtRuns = computed(() => this.distinctRuns(this.compiledAtTimes()));

  readonly compiledAt = computed(() => this.compiledAtRuns()[0] ?? null);

  /** The two most recent runs; separated by a bullet, since each stamp already contains a comma. */
  readonly compiledAtLabel = computed(() =>
    this.compiledAtRuns()
      .slice(0, VscListViewComponent.RUNS_SHOWN)
      .map((iso) => this.formatCompiledAt(iso))
      .join(' · '),
  );

  readonly compiledAtHiddenCount = computed(() => Math.max(0, this.compiledAtRuns().length - VscListViewComponent.RUNS_SHOWN));

  /** Runs beyond the first two, listed in the dialog behind the info icon. */
  readonly compiledAtOtherRuns = computed(() =>
    this.compiledAtRuns()
      .slice(VscListViewComponent.RUNS_SHOWN)
      .map((iso) => this.formatCompiledAt(iso)),
  );

  readonly compiledAtDialogOpen = signal(false);

  /** Compile stamps this far apart (ms) or less are treated as the same run. */
  private static readonly RUN_TOLERANCE_MS = 60_000;
  private static readonly RUNS_SHOWN = 2;

  readonly filteredRows = computed(() => {
    const codes = this.geoFilterCodes();
    if (codes === null) return this.rows();
    const codeSet = new Set(codes);
    return this.rows().filter((r) => codeSet.has(r.vendorCode));
  });

  private readonly loadTrigger$ = new Subject<{ month: string; year: number }>();
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.loadTrigger$
      .pipe(
        switchMap(({ month, year }) => {
          this.isLoading.set(true);
          this.dataError.set(null);
          this.rows.set([]);
          return forkJoin({
            scores: this.scorecardService.postGscView({ vendorCodes: this.shopCodes, month, year }),
            geo: this.geoService.getIndex(),
          }).pipe(
            catchError((err: unknown) => {
              const body = (err as { error?: unknown })?.error;
              const msg = typeof body === 'string' && body.trim() ? body.trim() : 'Failed to load vendor scores. Please try again.';
              this.dataError.set(msg);
              this.isLoading.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ scores, geo }) => {
        this.compiledAtTimes.set(scores.scoreCalculatedAt ?? []);
        this.rows.set(this.buildRows(scores.items, geo));
        this.isLoading.set(false);
      });

    effect(() => {
      const { month, year } = parseMonthLabel(this.selectedMonth());
      this.loadTrigger$.next({ month, year });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.periodDropdownOpen() && !this.periodContainer?.nativeElement.contains(event.target)) {
      this.periodDropdownOpen.set(false);
    }
  }

  togglePeriodDropdown(): void {
    this.periodDropdownOpen.update((v) => !v);
  }

  selectMonth(month: string): void {
    this.selectedMonth.set(month);
    this.periodDropdownOpen.set(false);
  }

  /**
   * The rows exactly as the grid is showing them — same geography filter, same sort, but every page
   * rather than the one on screen. Falls back to the filtered set while the grid is not rendered.
   */
  exportRows(): VscListRow[] {
    return this.grid()?.sortedRows() ?? this.filteredRows();
  }

  /** Fired by mc-list for a click or a keyboard Enter/Space on one of the Downloads items. */
  onDownloadSelected(event: Event): void {
    const value = (event as CustomEvent<{ item?: { value?: string } }>).detail?.item?.value;

    if (value === 'summary') void this.onDownloadSummary();
    else if (value === 'detailed') this.onDownloadDetailed();
  }

  async onDownloadSummary(): Promise<void> {
    const rows = this.exportRows();
    if (rows.length === 0) {
      this.toastr.showToastr('There is nothing to download for this selection.', 'info');
      return;
    }

    this.isDownloading.set(true);
    try {
      const month = this.selectedMonth();
      downloadBlob(await buildVscSummaryWorkbook(rows, month), vscSummaryFileName(month));
    } catch (err: unknown) {
      console.error('Vendor score card summary download failed', err);
      this.toastr.showToastr('Failed to prepare the download. Please try again.', 'error');
    } finally {
      this.isDownloading.set(false);
    }
  }

  onDownloadDetailed(): void {
    this.toastr.showToastr('Detailed download is under development', 'info');
  }

  onGeoShopCodesChange(codes: string[]): void {
    this.geoFilterCodes.set(codes.length > 0 ? codes : null);
  }

  formatCompiledAt(iso: string): string {
    const d = new Date(iso);
    const day = d.getDate();
    const s = ['th', 'st', 'nd', 'rd'];
    const v = day % 100;
    const suffix = s[(v - 20) % 10] ?? s[v] ?? s[0];
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const year = d.getFullYear();
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${day}${suffix} ${month} ${year}, ${time}`;
  }

  openCompiledAtDialog(): void {
    this.compiledAtDialogOpen.set(true);
  }

  closeCompiledAtDialog(): void {
    this.compiledAtDialogOpen.set(false);
  }

  /** Newest first, collapsing stamps that fall within RUN_TOLERANCE_MS of the one kept before them. */
  private distinctRuns(isoStamps: string[]): string[] {
    const sorted = isoStamps
      .filter((iso) => !Number.isNaN(new Date(iso).getTime()))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    const runs: string[] = [];
    for (const iso of sorted) {
      const last = runs.at(-1);
      const kept = last ? new Date(last).getTime() : null;
      if (kept === null || kept - new Date(iso).getTime() > VscListViewComponent.RUN_TOLERANCE_MS) {
        runs.push(iso);
      }
    }
    return runs;
  }

  private buildRows(items: VendorScoreGscViewItem[], geo: GeoIndex): VscListRow[] {
    return this.shopCodes.map((code) => {
      const shopKey = `shop:${code}`;
      const vendorName = (this.shopNameMap.get(code) ?? geo.byKey.get(shopKey)?.node.name ?? code).trim();
      const countryKey = geo.ancestorsOf.get(shopKey)?.find((k) => k.startsWith('country:'));
      const country = countryKey ? (geo.byKey.get(countryKey)?.node.name ?? '') : '';
      const item = items.find((i) => i.vendorCode === code);
      return {
        vendorCode: code,
        vendorName,
        country,
        workOrderCount: item?.workOrderCount ?? null,
        overallScore: item?.overallScore ?? null,
        leadTimeScore: item?.leadTimeScore ?? null,
        processComplianceScore: item?.processComplianceScore ?? null,
        eEIRScore: item?.eEIRScore ?? null,
        auditResultScore: item?.auditResultScore ?? null,
      };
    });
  }

  private formatMonth(d: Date): string {
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  }

  /** Months from the latest available month back to Jan 2025, newest first. */
  private buildMonthOptions(): string[] {
    const now = new Date();
    const latest = new Date(now.getFullYear(), now.getMonth() - this.monthOffset, 1);
    const count = (latest.getFullYear() - 2025) * 12 + latest.getMonth() + 1;
    return Array.from({ length: Math.max(1, count) }, (_, i) => this.formatMonth(new Date(latest.getFullYear(), latest.getMonth() - i, 1)));
  }
}
